import { ApiClient } from '../api/ApiClient';
import { Config } from '../core/Config';
import { isUserActive, onActivityChange } from '../utils/device';

/**
 * Upper bound on the read time we report, matching the server's
 * `read_seconds` validation (`max:3600`). The tracker accumulates active
 * seconds unbounded, so a view left open for over an hour would otherwise send
 * a value the server rejects with a 422 — dropping the whole read time.
 */
const MAX_READ_SECONDS = 3600;

export class ReadTimeTracker {
    private api: ApiClient;
    private config: Config;
    private onAuthError?: (error: unknown) => void;
    private startTime: number | null = null;
    private elapsedSeconds: number = 0;
    private viewId: string | null = null;
    private isTracking: boolean = false;
    private intervalId: ReturnType<typeof setInterval> | null = null;
    private visibilityCleanup: (() => void) | null = null;

    /**
     * @param onAuthError notified when the read-time send fails (e.g. an expired
     *   session token), so the orchestrator can recover the session instead of
     *   401ing forever. stop() still swallows the error and resolves.
     */
    constructor(api: ApiClient, config: Config, onAuthError?: (error: unknown) => void) {
        this.api = api;
        this.config = config;
        this.onAuthError = onAuthError;
    }

    public start(viewId: string): void {
        if (this.isTracking) {
            this.config.log('Read time tracking already active');
            return;
        }

        this.viewId = viewId;
        this.startTime = Date.now();
        this.elapsedSeconds = 0;
        this.isTracking = true;

        // Track time only when the user is actually present (tab visible AND
        // window focused).
        if (isUserActive()) {
            this.startInterval();
        }

        // Pause/resume as the tab visibility or window focus changes.
        this.visibilityCleanup = onActivityChange((isActive) => {
            if (isActive) {
                this.startTime = Date.now();
                this.startInterval();
            } else {
                this.pauseInterval();
            }
        });

        this.config.log(`Read time tracking started for view:`, viewId);
    }

    public async stop(): Promise<number> {
        if (!this.isTracking || !this.viewId) {
            return 0;
        }

        this.pauseInterval();

        // Clamp to the server's `read_seconds` cap so a view held open for
        // over an hour reports the max instead of 422ing and losing the whole
        // read time.
        const totalSeconds = Math.min(this.elapsedSeconds, MAX_READ_SECONDS);

        // Send final read time to API
        if (totalSeconds > 0) {
            try {
                await this.api.trackReadTime(this.viewId, totalSeconds);
                this.config.log(`Read time tracked: ${totalSeconds}s for view:`, this.viewId);
            } catch (error) {
                this.config.error(`Failed to track read time:`, error);
                this.onAuthError?.(error);
            }
        }

        this.cleanup();

        return totalSeconds;
    }

    public getElapsedSeconds(): number {
        return this.elapsedSeconds;
    }

    private startInterval(): void {
        if (this.intervalId) {
            return;
        }

        this.intervalId = setInterval(() => {
            if (this.startTime !== null && isUserActive()) {
                const now = Date.now();
                const secondsSinceStart = Math.floor((now - this.startTime) / 1000);

                if (secondsSinceStart > 0) {
                    this.elapsedSeconds += secondsSinceStart;
                    this.startTime = now;
                }
            }
        }, 1000);
    }

    private pauseInterval(): void {
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }

        // Finalize the segment that was being timed. `startTime` is only set
        // while actively counting, so whenever it's set we add the elapsed time —
        // including the final segment right before a blur/visibility change (this
        // is the moment pauseInterval is usually called, when the user is no
        // longer "active", so we must NOT gate this on isUserActive()).
        if (this.startTime !== null) {
            const now = Date.now();
            const secondsSinceStart = Math.floor((now - this.startTime) / 1000);
            if (secondsSinceStart > 0) {
                this.elapsedSeconds += secondsSinceStart;
            }
        }

        this.startTime = null;
    }

    private cleanup(): void {
        this.pauseInterval();

        if (this.visibilityCleanup) {
            this.visibilityCleanup();
            this.visibilityCleanup = null;
        }

        this.viewId = null;
        this.isTracking = false;
        this.elapsedSeconds = 0;
    }
}
